import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { keys, values } from 'lodash';
import { Box, Checkbox } from '@chakra-ui/react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getFacetedRowModel,
  getFacetedMinMaxValues,
  getFacetedUniqueValues,
  ColumnMeta,
  Table,
  ColumnFiltersState,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { getValue, isEmpty, mergeWithArray, strNomalize } from '../../helpers';
import { TColumn } from './DynamicTable';
import { TState, useActions } from '../../redux/store';
import useFilters from './tableComponents/useFilters';

type TTable = {
  data: any[];
  columns: TColumn[];
  onLoad?: () => void;
  editEntryKey?: string;
  hideButtons?: boolean;
  noResize?: boolean;
  onEditClick?: any;
  pendingKey: string;
  noActions?: boolean;
  formatData?: (data: any) => Record<string, any>;
};

export const NB_WIDTH = 60;
export const ACTION_WIDTH = 80;
export const DEFAULT_WIDTH = 115;

let timeout: any;

const computeWidths = (
  columns: any[],
  withNavWidth?: boolean,
  noActions?: boolean,
) => {
  const hasComms = !!columns.find((c) => c.id === 'crm_comm');
  const commWidth = hasComms ? DEFAULT_WIDTH * 3 : 0;

  const defaultSizes = [
    NB_WIDTH,
    ...columns.map((c) => (c.id === 'crm_comm' ? commWidth : DEFAULT_WIDTH)),
    noActions ? 0 : ACTION_WIDTH,
  ];

  const willOpen = withNavWidth === true;

  const navWidth = isEmpty(withNavWidth) ? 0 : willOpen ? 320 - 48 : -320 + 48;
  const maxWidth = getValue('#reactTableContainer', 'width') - navWidth;

  if (maxWidth === 0) {
    return null;
  }

  const last = defaultSizes.length - 1;
  const each =
    (maxWidth - defaultSizes[0] - defaultSizes[last] - commWidth) /
    (defaultSizes.length - (hasComms ? 3 : 2));

  const finals = defaultSizes.map((w, idx) =>
    idx === 0 || idx === last || w === commWidth
      ? w
      : each < DEFAULT_WIDTH
        ? DEFAULT_WIDTH
        : each,
  );

  return finals;
};

const numberSort = (rowA: any, rowB: any, columnId: string, desc: string) => {
  const a = rowA.values[columnId];
  const b = rowB.values[columnId];
  if (a === b) return 0;
  if (desc) {
    return a > b ? -1 : 1;
  } else {
    return a > b ? 1 : -1;
  }
};

const NBHeader = ({ table }: { table: Table<any> }) => {
  const columnFilters = useSelector((state: TState) => state.columnFilters);

  const end = columnFilters.find((f) => f.id === 'rowNb')?.value || undefined;
  const rows = end ? table.getSortedRowModel().rows.slice(0, end) : [];

  return (
    <Box cursor='default'>
      N°
      <Checkbox
        ml={2}
        isChecked={
          end
            ? rows.filter((r) => r.getIsSelected()).length === rows.length
            : table.getIsAllRowsSelected()
        }
        onChange={(e) =>
          end
            ? rows.forEach((r) => r.toggleSelected(e.target.checked))
            : table.toggleAllRowsSelected(e.target.checked)
        }
      />
    </Box>
  );
};

const useTableContainer = ({
  noResize,
  data,
  columns,
  editEntryKey,
  onLoad,
  onEditClick: oec,
  pendingKey,
  noActions,
  formatData,
}: TTable) => {
  const globalFilter = useSelector((state: TState) => state.globalFilter);
  const columnFilters = useSelector((state: TState) => state.columnFilters);
  const isHovered = useSelector((state: TState) => state.isHovered);
  const pendings = useSelector((state: TState) => state.pendings);
  const isMobile = useSelector((state: TState) => state.isMobile);

  const {
    setModalType,
    setRowSelected,
    setRowData,
    setColumnFilters,
    setModalData,
  } = useActions();

  const filterFn = useFilters();

  const [columnSizing, setColumnSizing] = useState<any>(null);
  const [widthSize, setWidthSize] = useState(window.innerWidth);
  const [rowSelection, setRowSelection] = useState({});

  const { hidedColumns, visibleCol } = columns.reduce(
    (prev, col: any) =>
      mergeWithArray(
        prev,
        col.meta.hide
          ? { hidedColumns: { [col.id]: false } }
          : { visibleCol: [col] },
      ),
    { hidedColumns: {}, visibleCol: [] },
  );

  const finalCol: any[] = useMemo(
    () =>
      !columns?.length
        ? []
        : [
            {
              header: ({ table }: any) => <NBHeader table={table} />,
              id: 'N°',
              accessorFn: (row: any, rowIdx: number) => rowIdx + 1,
              sortType: numberSort,
              enableSorting: false,
              enableResizing: false,
              meta: {
                _id: 'tableIndex',
                tableIndex: true,
              } as ColumnMeta<any, any>,
            },
            ...columns.flatMap((col: any) =>
              col.meta?.type === 'number'
                ? {
                    ...col,
                    sortType: numberSort,
                  }
                : col,
            ),
            noActions || isMobile
              ? null
              : {
                  header: 'Actions',
                  id: 'actions',
                  sortType: numberSort,
                  enableSorting: false,
                  enableResizing: false,
                  meta: {
                    _id: 'actions',
                    cellDisplay: true,
                  } as ColumnMeta<any, any>,
                },
          ].filter((f) => f),
    [columns],
  );

  const table = useReactTable({
    data,
    defaultColumn: {
      filterFn: filterFn(finalCol),
      sortingFn: (rowA, rowB, columnId) => {
        const valueA = `${rowA.renderValue(columnId) ?? '-'}`.replace(
          /\s/gi,
          '',
        );
        const valueB = `${rowB.renderValue(columnId) ?? '-'}`.replace(
          /\s/gi,
          '',
        );

        if (valueA === valueB) return 0;

        if (valueA === '-') return 1;
        if (valueB === '-') return -1;

        return strNomalize(valueA) < strNomalize(valueB) ? -1 : 1;
      },
    },
    columns: finalCol,
    columnResizeMode: 'onChange',
    state: {
      columnPinning: {
        left: ['N°'],
        right: ['actions'],
      },
      rowSelection,
      columnFilters: columnFilters as ColumnFiltersState,
      columnVisibility: hidedColumns,
    },
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    onColumnFiltersChange: setColumnFilters,
    globalFilterFn: (row, columnId, filterValue) => {
      if (!filterValue) {
        return true;
      }

      const cells = row.getAllCells();

      if (columnId === cells[0].column.id) {
        const norm = values(
          cells.map((cell) =>
            cell.column.id === 'N°'
              ? ''
              : strNomalize(`${cell.renderValue()}`.replace(/ /g, '')),
          ),
        ).join('__');

        return norm.includes(strNomalize(filterValue.replace(/ /g, '')));
      } else {
        return false;
      }
    },
  });

  const parentRef = useRef<any>();

  const { getHeaderGroups } = table;

  const end = columnFilters.find((f) => f.id === 'rowNb')?.value || undefined;
  const rows = useMemo(
    () => table.getSortedRowModel().rows.slice(0, end),
    [table.getSortedRowModel()],
  );

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => (noActions ? 35 : 56),
    overscan: 5,
  });

  useEffect(() => {
    const handleResize = () => {
      setWidthSize(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const resize = (navWidth?: boolean) => {
    const minSizes = computeWidths(visibleCol, navWidth, noActions);

    if (minSizes?.length) {
      getHeaderGroups()[0].headers.map((h, i) => {
        h.column.columnDef.minSize = minSizes[i];
        h.column.columnDef.size = minSizes[i];
      });

      setColumnSizing(minSizes);
    }

    return minSizes;
  };

  useEffect(() => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      resize();
    }, 50);
  }, [widthSize, finalCol]);

  useEffect(() => {
    if (columnSizing === null) return;
    resize(isHovered);
  }, [isHovered]);

  useEffect(() => {
    setRowSelection({});
  }, [data]);

  useEffect(() => {
    onLoad?.();
  }, [editEntryKey]);

  useEffect(() => {
    table.setGlobalFilter(globalFilter);
  }, [globalFilter]);

  useEffect(() => {
    setRowSelected(
      keys(rowSelection).map(
        (k) => table.getCoreRowModel().rowsById[k].original._id,
      ),
    );
  }, [rowSelection]);

  const onEditClick = async (row: any) => {
    oec?.(row);

    setModalData();
    setRowData(formatData ? formatData(row.original) : row.original);
    setModalType(editEntryKey || 'editEntry');
  };

  const isPending = pendings[pendingKey] > 0 || !columnSizing?.length;

  const props = useMemo(
    () => ({
      parentRef,
      table,
      totalSize: rowVirtualizer.getTotalSize(),
      rowVirtualizer,
      noResize,
      isPending,
      rows,
      getHeaderGroups,
      onEditClick,
    }),
    [rows, noResize, isPending, columnSizing],
  );

  return props;
};

export type TTableContainer = ReturnType<typeof useTableContainer>;
export default useTableContainer;
